/**
 * Score how well a live paragraph matches a clause search string.
 * Shared prefixes (e.g. 9.2 vs 9.4) must lose to a distinctive tail.
 * Leftover original_text from another clause must not win the pick.
 */

export const STRONG_PARAGRAPH_MATCH_SCORE = 10_000;
/** Min length for live-text locate / submit guards (short CRs like "הצהרת המממן."). */
export const MIN_CLAUSE_SEARCH_LEN = 8;
// qr-short-para-match-v1

function commonPrefixLength(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i += 1;
  return i;
}

export function scoreParagraphAgainstSearch(paraNorm: string, searchNorm: string): number {
  if (!paraNorm || !searchNorm) return 0;

  if (paraNorm === searchNorm) {
    return 1_000_000 + paraNorm.length;
  }

  // Short live paragraphs (e.g. "הצהרת המממן.") must still match a QR target
  // that is exactly live + suffix — otherwise submit aborts as "not this clause".
  if (paraNorm.length >= 8 && searchNorm.startsWith(paraNorm)) {
    return 220_000 + paraNorm.length;
  }

  if (searchNorm.length < 40) {
    if (paraNorm.includes(searchNorm) && searchNorm.length >= 8) {
      return 200_000 + searchNorm.length;
    }
    return 0;
  }

  if (paraNorm.includes(searchNorm)) {
    return 500_000 + searchNorm.length;
  }
  if (searchNorm.includes(paraNorm) && paraNorm.length >= 8) {
    // Live shorter than the needle is OK only when it is a substantial
    // fragment — not a ~45-char party line inside a long CR
    // (track_changes_202) or a 12-char heading (track_changes_50).
    const minFragment = Math.max(40, Math.floor(searchNorm.length * 0.5));
    if (paraNorm.length >= minFragment) {
      return 250_000 + paraNorm.length;
    }
  }

  const shared = commonPrefixLength(paraNorm, searchNorm);
  const searchTail = searchNorm.slice(shared).trim();
  const paraTail = paraNorm.slice(shared);
  if (searchTail.length >= 8) {
    const tailNeedle = searchTail.slice(0, Math.min(40, searchTail.length));
    if (paraTail.includes(tailNeedle)) {
      return 100_000 + shared + tailNeedle.length;
    }
  }

  const endNeedle = searchNorm.slice(-Math.min(40, searchNorm.length)).trim();
  if (endNeedle.length >= 24 && paraNorm.includes(endNeedle)) {
    return 180_000 + endNeedle.length;
  }

  const head = searchNorm.slice(0, 80);
  if (paraNorm.length >= 20 && head.length >= 40 && paraNorm.includes(head)) {
    // Distinctive CR tail missing from this paragraph → twin / wrong clause
    // (track_changes_36 / 9.4 must not locate onto longer 9.2 live text).
    if (
      searchTail.length >= 24 &&
      !paraTail.includes(searchTail.slice(0, Math.min(40, searchTail.length)))
    ) {
      return shared;
    }
    // Truncated CR head (88 vs live 245) is a real hit. Same-length twins (9.2 vs 9.4)
    // only share a prefix — keep that weak so the distinctive tail can win.
    if (searchNorm.length < paraNorm.length * 0.7) {
      return 200_000 + head.length;
    }
    return shared;
  }
  return 0;
}

export function isStrongParagraphSearchMatch(paraNorm: string, searchNorm: string): boolean {
  return scoreParagraphAgainstSearch(paraNorm, searchNorm) >= STRONG_PARAGRAPH_MATCH_SCORE;
}

/** Long searches require a distinctive tail; short needles only need a positive score. */
export function isAcceptableParagraphSearchPick(score: number, searchNorm: string): boolean {
  if (score <= 0) return false;
  if (searchNorm.length >= 40) return score >= STRONG_PARAGRAPH_MATCH_SCORE;
  return true;
}

/** True when the live paragraph is a strong match for any of the clause search strings. */
export function paragraphMatchesAnySearch(paraNorm: string, searchNorms: string[]): boolean {
  for (const searchNorm of searchNorms) {
    if (!searchNorm) continue;
    if (isAcceptableParagraphSearchPick(scoreParagraphAgainstSearch(paraNorm, searchNorm), searchNorm)) {
      return true;
    }
  }
  return false;
}

function foldSearchText(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Card-displayed client request wins over leftover Module B / original_text
 * (e.g. a בדק בית cell stored on the same clause).
 */
export function pickGoToDisplayedPrimary(args: {
  clientRequestPlainText: string;
  moduleBAnchor: string;
  preferModuleBAnchor: boolean;
  fallbackSearchText: string;
  originalText: string;
}): string {
  const cr = foldSearchText(args.clientRequestPlainText);
  if (cr.length >= 15) return cr;
  const moduleB = foldSearchText(args.moduleBAnchor);
  if (args.preferModuleBAnchor && moduleB.length >= 15) return moduleB;
  const fallback = foldSearchText(args.fallbackSearchText);
  if (fallback.length >= 15) return fallback;
  const original = foldSearchText(args.originalText);
  if (original.length >= 15) return original;
  return cr || moduleB || fallback || original;
}

export function clauseSearchTextBelongsToDisplayed(displayed: string, candidate: string): boolean {
  const displayedNorm = foldSearchText(displayed);
  const candidateNorm = foldSearchText(candidate);
  // Short CRs (track_changes_18: "הצהרת המממן.") must stay eligible for the
  // submit live-paragraph guard — a 15-char floor dropped them and aborted apply.
  if (candidateNorm.length < 8) return false;
  if (displayedNorm.length < 8) return true;
  if (displayedNorm.length < 15 || candidateNorm.length < 15) {
    return (
      candidateNorm === displayedNorm ||
      candidateNorm.startsWith(displayedNorm) ||
      displayedNorm.startsWith(candidateNorm)
    );
  }
  return (
    paragraphMatchesAnySearch(candidateNorm, [displayedNorm]) ||
    paragraphMatchesAnySearch(displayedNorm, [candidateNorm])
  );
}

/**
 * Word `body.search` often misses a legal opening (RTL / punctuation) but finds
 * the distinctive tail. Light navigation keeps only the first two slices.
 */
export function distinctiveBodySearchSlices(searchNorm: string): string[] {
  const slices: string[] = [];
  const seen = new Set<string>();
  const add = (value: string) => {
    const cleaned = (value || '').replace(/\s+/g, ' ').trim();
    if (cleaned.length >= 20 && !seen.has(cleaned)) {
      seen.add(cleaned);
      slices.push(cleaned);
    }
  };
  if (searchNorm.length > 80) add(searchNorm.slice(-80));
  if (searchNorm.length > 120) add(searchNorm.slice(-120));
  if (searchNorm.length > 80) {
    const midStart = Math.min(
      Math.max(40, Math.floor(searchNorm.length * 0.3)),
      searchNorm.length - 40,
    );
    add(searchNorm.slice(midStart, midStart + 40));
  }
  if (searchNorm.length > 120) add(searchNorm.slice(0, 120));
  if (searchNorm.length > 80) add(searchNorm.slice(0, 80));
  if (searchNorm.length <= 120) add(searchNorm);
  return slices;
}

export function pickBestParagraphIndex(
  paraNorms: string[],
  searchNorm: string,
  ooxmlTargetNorm?: string,
): { index: number; score: number } {
  let bestIndex = -1;
  let bestScore = 0;
  for (let i = 0; i < paraNorms.length; i++) {
    let score = scoreParagraphAgainstSearch(paraNorms[i] || '', searchNorm);
    if (ooxmlTargetNorm && paraNorms[i] === ooxmlTargetNorm) {
      score += 50;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }
  return { index: bestIndex, score: bestScore };
}
